// Renders one tool invocation in the chat. Handles the 4 lifecycle states
// emitted by AI SDK v6 useChat: input-streaming, input-available,
// output-available, output-error.

import { selectSlide } from "../lib/office";

interface ToolPart {
  type: string; // "tool-read_selection" etc
  toolCallId?: string;
  state?: "input-streaming" | "input-available" | "output-available" | "output-error";
  input?: unknown;
  output?: unknown;
  errorText?: string;
}

const TOOL_LABELS: Record<string, { running: string; done: (out: unknown, inp: unknown) => string }> = {
  read_selection: {
    running: "Selectie lezen…",
    done: (out) => {
      const o = out as { slides?: { slide: number }[]; shapes?: unknown[] } | undefined;
      if (!o?.slides?.length) return "Selectie gelezen";
      return `Selectie gelezen — dia ${o.slides.map((s) => s.slide).join(", ")}${o.shapes?.length ? ` (${o.shapes.length} shapes)` : ""}`;
    },
  },
  read_slide: {
    running: "Dia lezen…",
    done: (out) => {
      const o = out as { slide?: number; shapes?: unknown[] } | undefined;
      return o?.slide ? `Dia ${o.slide} gelezen (${o.shapes?.length ?? 0} shapes)` : "Dia gelezen";
    },
  },
  get_presentation_info: {
    running: "Presentatie scannen…",
    done: (out) => {
      const o = out as { slideCount?: number; title?: string } | undefined;
      return o ? `Presentatie: ${o.slideCount ?? 0} dia's${o.title ? ` — "${o.title}"` : ""}` : "Presentatie info";
    },
  },
  get_presentation_theme: {
    running: "Presentatiethema lezen…",
    done: (out) => {
      const o = out as { sampledFonts?: unknown[] } | undefined;
      return `Presentatiethema gelezen${o?.sampledFonts?.length ? ` (${o.sampledFonts.length} lettertypen)` : ""}`;
    },
  },
  add_slide: {
    running: "Dia toevoegen…",
    done: (out) => {
      const o = out as { slide?: number; layout?: string } | undefined;
      return o?.slide ? `Dia ${o.slide} toegevoegd${o.layout ? ` (${o.layout})` : ""}` : "Dia toegevoegd";
    },
  },
  delete_slide: {
    running: "Dia verwijderen…",
    done: (_, inp) => {
      const i = inp as { slide?: number } | undefined;
      return i?.slide ? `Dia ${i.slide} verwijderd` : "Dia verwijderd";
    },
  },
  select_slide: {
    running: "Navigeren…",
    done: (_, inp) => {
      const i = inp as { slide?: number } | undefined;
      return i?.slide ? `Genavigeerd naar dia ${i.slide}` : "Genavigeerd";
    },
  },
  add_text_box: {
    running: "Tekstvak toevoegen…",
    done: (out, inp) => {
      const o = out as { slide?: number } | undefined;
      const i = inp as { text?: string } | undefined;
      const preview = i?.text ? `"${i.text.slice(0, 40)}${i.text.length > 40 ? "…" : ""}"` : "";
      return o?.slide ? `Tekstvak op dia ${o.slide} ${preview}`.trim() : "Tekstvak toegevoegd";
    },
  },
  add_shape: {
    running: "Shape toevoegen…",
    done: (out) => {
      const o = out as { slide?: number; type?: string } | undefined;
      return o?.slide ? `Shape (${o.type ?? "?"}) op dia ${o.slide}` : "Shape toegevoegd";
    },
  },
  set_shape_text: {
    running: "Tekst schrijven…",
    done: (out, inp) => {
      const o = out as { slide?: number } | undefined;
      const i = inp as { shape?: string } | undefined;
      return o?.slide ? `Tekst van "${i?.shape}" op dia ${o.slide} bijgewerkt` : "Tekst bijgewerkt";
    },
  },
  format_shape: {
    running: "Opmaken…",
    done: (out, inp) => {
      const o = out as { slide?: number } | undefined;
      const i = inp as { shape?: string } | undefined;
      return o?.slide ? `"${i?.shape}" op dia ${o.slide} opgemaakt` : "Opmaak toegepast";
    },
  },
  delete_shape: {
    running: "Shape verwijderen…",
    done: (_, inp) => {
      const i = inp as { shape?: string; slide?: number } | undefined;
      return i?.shape ? `Shape "${i.shape}" verwijderd van dia ${i.slide}` : "Shape verwijderd";
    },
  },
  get_powerpoint_capabilities: { running: "PowerPoint-capaciteiten controleren…", done: () => "PowerPoint-capaciteiten gecontroleerd" },
  move_slide: { running: "Dia verplaatsen…", done: (_, inp) => `Dia verplaatst naar positie ${(inp as { to?: number })?.to ?? "?"}` },
  duplicate_slide: { running: "Dia dupliceren…", done: (out) => `Dia ${(out as { slide?: number })?.slide ?? ""} gedupliceerd`.trim() },
  apply_slide_layout: { running: "Layout toepassen…", done: (_, inp) => `Layout ${(inp as { layout?: string })?.layout ?? ""} toegepast`.trim() },
  get_slide_image: { running: "Dia renderen…", done: (_, inp) => `Dia ${(inp as { slide?: number })?.slide ?? ""} gerenderd`.trim() },
  export_slides: { running: "Dia's exporteren…", done: () => "Dia's geëxporteerd" },
  import_slides: { running: "Dia's importeren…", done: () => "Dia's geïmporteerd" },
  set_slide_background: { running: "Dia-achtergrond aanpassen…", done: () => "Dia-achtergrond aangepast" },
  add_image: { running: "Afbeelding toevoegen…", done: () => "Afbeelding toegevoegd" },
  add_line: { running: "Lijn toevoegen…", done: () => "Lijn toegevoegd" },
  add_table: { running: "Tabel toevoegen…", done: () => "Tabel toegevoegd" },
  read_table: { running: "Tabel lezen…", done: () => "Tabel gelezen" },
  update_table_cell: { running: "Tabelcel bijwerken…", done: () => "Tabelcel bijgewerkt" },
  group_shapes: { running: "Shapes groeperen…", done: () => "Shapes gegroepeerd" },
  ungroup_shapes: { running: "Groep opheffen…", done: () => "Groep opgeheven" },
  select_shapes: { running: "Shapes selecteren…", done: () => "Shapes geselecteerd" },
  arrange_shapes: { running: "Shapes uitlijnen…", done: () => "Shapes uitgelijnd" },
  set_shape_z_order: { running: "Laagvolgorde aanpassen…", done: () => "Laagvolgorde aangepast" },
  set_shape_metadata: { running: "Shape-eigenschappen aanpassen…", done: () => "Shape-eigenschappen aangepast" },
  set_shape_hyperlink: { running: "Hyperlink instellen…", done: () => "Hyperlink ingesteld" },
  run_powerpoint_script: {
    running: "Script uitvoeren…",
    done: (_, inp) => {
      const i = inp as { description?: string } | undefined;
      return i?.description ? `Script: ${i.description}` : "Script uitgevoerd";
    },
  },
};

export function ToolCallCard({ part }: { part: ToolPart }) {
  const name = part.type.replace(/^tool-/, "");
  const meta = TOOL_LABELS[name] ?? {
    running: `${name}…`,
    done: () => name,
  };
  const state = part.state ?? "input-streaming";

  const icon =
    state === "input-streaming" || state === "input-available" ? (
      <span className="animate-pulse">●</span>
    ) : state === "output-error" ? (
      <span className="text-destructive">✕</span>
    ) : (
      <span className="text-emerald-600">✓</span>
    );

  const label =
    state === "output-available"
      ? meta.done(part.output, part.input)
      : state === "output-error"
        ? `${name} mislukt: ${part.errorText ?? "onbekende fout"}`
        : meta.running;

  const slide =
    (part.output as { slide?: number } | undefined)?.slide ??
    (part.input as { slide?: number } | undefined)?.slide;

  return (
    <div className="my-1 inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs text-fg">
      {icon}
      <span>{label}</span>
      {state === "output-available" && typeof slide === "number" && name !== "select_slide" && name !== "delete_slide" && (
        <button
          onClick={() => selectSlide(slide).catch(() => {})}
          title={`Ga naar dia ${slide}`}
          className="text-muted-fg hover:text-fg"
        >
          ↗
        </button>
      )}
    </div>
  );
}
